import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { DriverApi, DriverDTO } from './DriverApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPlus, faArrowLeft, faIdBadge } from '@fortawesome/free-solid-svg-icons';

type KPI = { title: string; value: string | number; icon?: any; accent?: string };

export const DriverDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [tenantId] = useState<string>(() => user.tenantId ||  '');
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState<DriverDTO[]>([]);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    DriverApi.fetchAll(tenantId)
      .then((res) => setDrivers(res.data?.content || res.data || []))
      .catch((e) => console.error('Failed to fetch drivers:', e))
      .finally(() => setLoading(false));
  }, [tenantId]);

  const kpis: KPI[] = useMemo(() => {
    const total = drivers.length;
    const active = drivers.filter((d) => d.isActive !== false).length;
    const expiringSoon = drivers.filter((d) => {
      const dt = d.licenseValidTill ? new Date(d.licenseValidTill) : undefined;
      if (!dt) return false;
      const days = (dt.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
      return days >= 0 && days <= 30;
    }).length;

    return [
      { title: 'Total Drivers', value: total, icon: faUsers, accent: 'text-blue-600' },
      { title: 'Active Drivers', value: active, icon: faIdBadge, accent: 'text-green-600' },
      { title: 'License Expiring ≤30d', value: expiringSoon, icon: faIdBadge, accent: 'text-amber-600' },
    ];
  }, [drivers]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Driver Dashboard</h1>
        <div className="flex gap-2">
          <button className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded px-3 py-2" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back
          </button>
          <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" onClick={() => navigate('/vms/driver/create')}>
            <FontAwesomeIcon icon={faPlus} className="mr-2" /> New Driver
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {loading ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </>
        ) : (
          kpis.map((k) => (
            <Card key={k.title}>
              <CardContent className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{k.title}</p>
                  <p className="text-2xl font-semibold">{k.value}</p>
                </div>
                {k.icon && <FontAwesomeIcon icon={k.icon} className={`text-3xl ${k.accent}`} />}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Card>
        <CardContent className="flex gap-2">
          <button className="bg-white border rounded px-4 py-2 hover:bg-gray-50" onClick={() => navigate('/vms/driver/list')}>
            Open Driver List
          </button>
          <button className="bg-white border rounded px-4 py-2 hover:bg-gray-50" onClick={() => navigate('/vms/driver/create')}>
            Create Driver
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverDashboard;